import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "../../style/attendanceContent.css";
import { PageHeader, Card, EmptyState } from "../../components/admin/AdminUI";

const swalError = (text) =>
  Swal.fire({ text, icon: "error", background: "white", color: "black", confirmButtonColor: "#0d9488" });
const swalSuccess = (text) =>
  Swal.fire({ text, icon: "success", background: "white", color: "black", confirmButtonColor: "#0d9488" });

// Admin mengelola akun class assistant di sini. Admin hanya mengisi nama dan
// email — password dipasang assistant sendiri lewat OTP email, jadi admin
// tidak pernah mengetahui password siapa pun.
const ClassAssistantsPage = ({ setLocationAdmin }) => {
  const navigate = useNavigate();
  const address = useLocation();
  const [errorMe, setErrorMe] = useState(false);

  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const loginUrl = `${window.location.origin}/assistant/login`;

  const getMe = async () => {
    try {
      await axios.get("/meAdmin");
    } catch (error) {
      setErrorMe(true);
    }
  };

  const fetchAssistants = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/classassistants");
      setAssistants(res.data || []);
    } catch (error) {
      setAssistants([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLocationAdmin(address);
    getMe();
    fetchAssistants();
  }, []);

  useEffect(() => {
    if (errorMe) {
      Swal.fire({
        title: "Session Expired",
        text: "Please login to your account !",
        icon: "warning",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "Ok",
      });
      navigate("/loginAdmin");
    }
  }, [errorMe, navigate]);

  const handleAdd = async () => {
    if (!name.trim() || !email.trim()) return swalError("Name and email are required.");
    setSaving(true);
    try {
      const res = await axios.post("/classassistants", { name, email });
      swalSuccess(res.data.msg);
      setName("");
      setEmail("");
      fetchAssistants();
    } catch (error) {
      swalError(error.response?.data?.msg || "Failed to add class assistant.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (a) => {
    try {
      await axios.patch(`/classassistants/${a.uuid}`, { isActive: !a.isActive });
      fetchAssistants();
    } catch (error) {
      swalError(error.response?.data?.msg || "Failed to update account.");
    }
  };

  const handleDelete = async (a) => {
    const result = await Swal.fire({
      title: `Delete ${a.name}?`,
      text: "This account will no longer be able to log in. Attendance already recorded is not affected.",
      icon: "warning",
      showCancelButton: true,
      background: "white",
      color: "black",
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete",
    });
    if (!result.isConfirmed) return;
    try {
      await axios.delete(`/classassistants/${a.uuid}`);
      fetchAssistants();
    } catch (error) {
      swalError(error.response?.data?.msg || "Failed to delete account.");
    }
  };

  return (
    <div className="adm-main">
      <PageHeader
        title="Class Assistants"
        subtitle={
          <>
            Class assistants log in at{" "}
            <a href={loginUrl} style={{ color: "var(--adm-primary)", fontWeight: 600 }}>
              {loginUrl}
            </a>{" "}
            and can only open Class Sessions and Attendance Grid.
          </>
        }
      />

      <Card title="Add Class Assistant" subtitle="No password is set here. The assistant receives an email and sets their own password with an OTP sent to that address.">
        <div className="adm-form-grid adm-form-grid--3" style={{ alignItems: "end" }}>
          <div>
            <label className="form-label">Full name</label>
            <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Maria Christina" />
          </div>
          <div>
            <label className="form-label">Email</label>
            <input type="email" className="form-control" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="example@gmail.com" />
          </div>
          <div>
            <button type="button" className="adm-btn adm-btn--primary" disabled={saving} onClick={handleAdd}>
              <i className="bi bi-person-plus"></i>
              {saving ? "Adding..." : "Add Assistant"}
            </button>
          </div>
        </div>
      </Card>

      <Card title="Accounts" subtitle={`${assistants.length} account${assistants.length === 1 ? "" : "s"}`} flush className="adm-section">
        {loading ? (
          <EmptyState icon="bi-hourglass" title="Loading accounts…" />
        ) : assistants.length === 0 ? (
          <EmptyState icon="bi-people" title="No class assistant accounts yet" text="Add one with the form above." />
        ) : (
          <div className="adm-table-wrap">
            <table className="adm-table" style={{ minWidth: "640px" }}>
              <thead>
                <tr>
                  <th>Assistant</th>
                  <th>Password</th>
                  <th>Status</th>
                  <th className="adm-col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {assistants.map((a) => (
                  <tr key={a.uuid}>
                    <td>
                      <div className="adm-cell-title">{a.name}</div>
                      <div className="adm-cell-meta">{a.email}</div>
                    </td>
                    <td>
                      <span className={`adm-badge ${a.hasPassword ? "adm-badge--success" : "adm-badge--warning"}`}>
                        {a.hasPassword ? "Set" : "Not set yet"}
                      </span>
                    </td>
                    <td>
                      <span className={`adm-status ${a.isActive ? "adm-status--success" : "adm-status--danger"}`}>
                        {a.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="adm-col-actions">
                      <div className="adm-actions">
                        <button type="button" className="adm-btn adm-btn--secondary adm-btn--sm" onClick={() => handleToggle(a)}>
                          {a.isActive ? "Deactivate" : "Activate"}
                        </button>
                        <button
                          type="button"
                          className="adm-icon-btn adm-icon-btn--danger"
                          title="Delete account"
                          aria-label="Delete account"
                          onClick={() => handleDelete(a)}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ClassAssistantsPage;
